import type { SyntheticEvent } from "react";

import { useTopicItem } from "app/pages/activation/hooks/topics/useTopicItem";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageFilter } from "app/filters/ngFilters";
import { ActivationUnits, addPercent } from "app/pages/activation/helpers/format";

import { topicKeys } from "./automationKeys";
import { KEYWORD_PLACEHOLDER, ZERO } from "./constants";
import { Leader } from "./Leader/Leader";
import { Menu } from "./Menu/Menu";
import { MetadataTitle } from "./MetadataTitle/MetadataTitle";
import { PieGraph } from "./PieGraph/PieGraph";
import { Share } from "./Share/Share";
import { SHARE_LABEL, VOLUME_LABEL } from "./Share/texts";
import { StyledExcelIconButton, StyledSeparator, StyledShareWrapper, StyledTopicWrapper, StyledVolume } from "./styles";
import { Trends } from "./Trends/Trends";
import type { TopicViewSchema } from "./type";

export const Topic = ({ data, isViewerMode = false, isLastTopic }: TopicViewSchema) => {
    const {
        leaderIcon,
        id: topicId,
        volumeChange,
        pieChartData,
        isPreloading,
        isShared,
        sharedBy,
        name,
        keywordsCount = KEYWORD_PLACEHOLDER,
        shareOfVoice,
        shareOfVoiceChange,
        leaderName,
        trend,
        volume,
        leaderShareOfVoice,
        topicName,
    } = data;

    const {
        isLoading,
        isDisabled,
        isSuggestion,
        onClick,
        onMenuClick,
        onDownloadExcel,
        granularity,
        downloadingTopics,
        isKeywordsLocked,
    } = useTopicItem({
        topicId,
        isPreloading,
        isShared,
        keywordsCount,
    });

    return (
        <StyledTopicWrapper
            isLoading={isLoading}
            onClick={onClick}
            data-automation={topicKeys.container}
        >
            <MetadataTitle
                isLoading={isLoading}
                name={isLoading ? "" : name}
                topicName={isLoading ? "" : topicName}
                isSuggestion={isSuggestion}
                isShared={isShared}
                sharedBy={sharedBy}
                keywordsCount={keywordsCount}
            />

            <StyledShareWrapper>
                <Share
                    isLoading={isLoading}
                    label={translate(SHARE_LABEL)}
                    value={addPercent(percentageFilter()(shareOfVoice, 1))}
                    change={shareOfVoiceChange}
                    isDisabled={isDisabled}
                    metric={ActivationUnits.PercentPoints}
                    withSpace
                    name="share"
                />
            </StyledShareWrapper>

            <Leader
                isLoading={isLoading}
                share={leaderShareOfVoice}
                iconSrc={leaderIcon}
                domain={leaderName}
                isSuggested={isSuggestion}
            />

            <PieGraph
                chartData={pieChartData}
                isLoading={isLoading}
            />

            <StyledSeparator />

            {/* Volume */}
            <StyledVolume>
                <Share
                    isLoading={isLoading}
                    label={translate(VOLUME_LABEL, { granularity })}
                    value={isDisabled ? ZERO : abbrNumberFilter()(volume)}
                    change={volumeChange}
                    isDisabled={isDisabled}
                    metric={ActivationUnits.Percent}
                    name="volume"
                />

                <Trends
                    isLoading={isLoading}
                    trendData={trend}
                />
            </StyledVolume>

            {!isKeywordsLocked ? (
                <StyledExcelIconButton
                    type="flat"
                    iconName="excel"
                    isLoading={isLoading || downloadingTopics.includes(topicId)}
                    isDisabled={isLoading}
                    onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        onDownloadExcel(topicId, keywordsCount);
                    }}
                />
            ) : null}

            {!isViewerMode ? (
                <Menu
                    isLoading={isLoading}
                    onClick={onMenuClick}
                    isShared={isShared}
                    isLastTopic={isLastTopic}
                />
            ) : null}
        </StyledTopicWrapper>
    );
};
